import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

export const AuthContext = createContext();

// Include vendor store page pattern in public routes
const publicRoutes = [
  '/', 
  '/nearby', 
  '/login', 
  '/register', 
  '/add-strain', 
  '/terms', 
  '/contact',
  '/sales',
  '/courier',
  '/courier-request',
  '/courier-login',
  '/checkout',
  '/order-confirmation',
  // Make all vendor menu routes public
  '/vendor' // This will match any path that starts with /vendor
];

// Improved function to check if a route is public with better logging
const isPublicRoute = (path) => {
  console.log('Checking if path is public:', path);
  
  // Special case for paths with dynamic segments
  if (path.includes('/order-confirmation/')) {
    console.log('Order confirmation page detected, treating as public');
    return true;
  }
  
  // Handle vendor menu pages as public
  if (path.includes('/vendor/') && path.includes('/menu')) {
    console.log('Vendor menu page detected, treating as public');
    return true;
  }
  
  // Handle vendor dashboard pages as protected
  if (path === '/vendor-dashboard' || path.includes('/vendor/') && !path.includes('/menu')) {
    console.log('Vendor dashboard page detected, treating as protected');
    return false;
  }
  
  const result = publicRoutes.some(route => {
    if (typeof route === 'string') {
      // Check if the path starts with the route string
      const matches = route === path || (route !== '/' && path.startsWith(route));
      console.log(`String route check: ${route} matches ${path} ? ${matches}`);
      return matches;
    }
    // If it's a regex pattern
    const matches = route.test(path);
    console.log(`Regex route check: ${route} matches ${path} ? ${matches}`);
    return matches;
  });
  
  console.log('Final public route check result:', result);
  return result;
};

export const AuthProvider = ({ children }) => {
    const [auth, setAuth] = useState(() => {
        // Initialize auth from localStorage on page load
        const savedAuth = localStorage.getItem('auth');
        return savedAuth ? JSON.parse(savedAuth) : null;
    });
    const [loading, setLoading] = useState(true);

    // Helper function to clear auth state and cookies
    const clearAuthState = () => {
        console.log('Clearing auth state and cookies');
        setAuth(null);
        
        const isDevelopment = process.env.NODE_ENV === 'development';
        const cookieOptions = `Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT; ${
            !isDevelopment ? `Domain=.weedhurry.com;` : ''
        } ${!isDevelopment ? 'Secure; SameSite=None' : ''}`;
        
        document.cookie = `accessToken=;${cookieOptions}`;
        document.cookie = `refreshToken=;${cookieOptions}`;
    
        localStorage.removeItem('auth');
        localStorage.removeItem('accessToken');
        sessionStorage.clear();
    };

    // Function to log in the user
    const login = async (vendorId, accessToken) => {
        console.log('Setting auth state with vendorId:', vendorId);
        const authData = { vendorId };
        
        // Save to state and localStorage
        setAuth(authData);
        localStorage.setItem('auth', JSON.stringify(authData));
        
        // If token is provided, also save it to localStorage
        if (accessToken) {
            console.log('Saving access token to localStorage');
            localStorage.setItem('accessToken', accessToken);
        }
    };

    // Function to log out the user
    const logout = async () => {
        try {
            console.log('Starting logout process...');
            await api.post('/auth/logout', {}, { withCredentials: true });
    
            clearAuthState();
            console.log('Auth state and cookies cleared');
        } catch (error) {
            console.error('Logout failed:', error);
            clearAuthState();
        }
    };
    
    // Function to check authentication status with enhanced logging
    const checkAuthStatus = async () => {
        const currentPath = window.location.pathname;
        console.log('Checking auth status for path:', currentPath);
        console.log('Current cookies:', document.cookie);
        
        // Skip auth check for public routes
        if (isPublicRoute(currentPath)) {
            console.log('Public route detected, skipping auth check:', currentPath);
            setLoading(false);
            return;
        }

        console.log('Protected route detected, proceeding with auth check');
        
        try {
            // First try with stored token (either cookie or localStorage)
            console.log('Attempting to validate auth token');
            // FIXED: Removed '/api' prefix since it's now included in the base URL
            const response = await api.get('/auth/validate', {
                withCredentials: true
            });

            console.log('Validation response:', response.data);
            
            if (response.data?.success) {
                console.log('Auth validation successful for vendor:', response.data.vendorId);
                await login(response.data.vendorId);
            } else {
                console.log('Auth validation failed, attempting token refresh');
                try {
                    const refreshResponse = await api.post('/auth/refresh-token', {}, { 
                        withCredentials: true,
                        headers: { "Content-Type": "application/json" }
                    });
                    
                    console.log('Token refresh response:', refreshResponse.data);
                    
                    if (refreshResponse.data?.success) {
                        console.log('Token refresh successful');
                        // Save new token and update auth state
                        await login(
                            refreshResponse.data.vendorId, 
                            refreshResponse.data.accessToken
                        );
                    } else {
                        console.log('Token refresh failed, clearing auth state');
                        clearAuthState();
                    }
                } catch (refreshError) {
                    console.error('Token refresh failed:', refreshError);
                    clearAuthState();
                }
            }
        } catch (error) {
            console.error('Auth check failed:', error);
            clearAuthState();
        } finally {
            setLoading(false);
        }
    };

    // Update localStorage when auth state changes
    useEffect(() => {
        if (auth) {
            localStorage.setItem('auth', JSON.stringify(auth));
        }
    }, [auth]);

    // Check auth status on mount and route change
    useEffect(() => {
        console.log('AuthProvider initialized');
        
        const handleRouteChange = () => {
            console.log('Route changed, rechecking auth status');
            checkAuthStatus();
        };

        // Initial check
        checkAuthStatus();

        // Listen for route changes
        window.addEventListener('popstate', handleRouteChange);
        
        // Listen for history API changes
        const originalPushState = window.history.pushState;
        window.history.pushState = function() {
            originalPushState.apply(this, arguments);
            console.log('pushState called, rechecking auth status');
            checkAuthStatus();
        };

        return () => {
            window.removeEventListener('popstate', handleRouteChange);
            window.history.pushState = originalPushState;
        };
    }, []);

    const contextValue = {
        auth,
        login,
        logout,
        loading,
        checkAuthStatus,
        isPublicRoute // Export for testing/debugging
    };

    return (
        <AuthContext.Provider value={contextValue}>
            {loading ? <div>Loading...</div> : children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};